import { useMemo } from "react";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import {
  FORM_FIELDS,
  LOADED_VESSEL_CONDITIONS,
  yesNAOptions,
  yesNoNAOptions,
  type VesselNominationOutletContext,
} from "@features/vessel-nomination/add/constants";
import type { VesselNominationFormData } from "@features/vessel-nomination/add/schema/vesselNominationFormSchema";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { LANG_BASE } from "../PreArrivalQuestionnaire";

const ToxicComponent = () => {
  const { t } = useTranslation();
  const { formData, updateFormData } = useOutletContext<VesselNominationOutletContext>();

  const vesselCondition = formData.watch(FORM_FIELDS.VESSEL_CONDITION);
  const isVesselConditionLoaded = useMemo(() => {
    return LOADED_VESSEL_CONDITIONS.includes(vesselCondition);
  }, [vesselCondition]);

  return (
    <FormProvider {...formData}>
      <FormSection
        title={t(`${LANG_BASE}.sections.toxic-component`)}
        className="grid-cols-1 lg:grid-cols-2 xl:grid-cols-6"
      >
        {isVesselConditionLoaded && (
          <>
            <div className="xl:col-span-2">
              <FormItem<VesselNominationFormData, typeof FORM_FIELDS.FLASH_POINT>
                testId="nomination-flash-point-input"
                name={FORM_FIELDS.FLASH_POINT}
                type="number"
                label={t(`${LANG_BASE}.fields.flash-point`)}
                onFieldChange={updateFormData}
                maxDigits={6}
                decimals={3}
                adornment="°C"
                thousandSeparator
                canBeginWithZero={false}
              />
            </div>
            <div className="xl:col-span-2">
              <FormItem<VesselNominationFormData, typeof FORM_FIELDS.SULPHUR>
                testId="nomination-sulphur-input"
                name={FORM_FIELDS.SULPHUR}
                type="number"
                label={t(`${LANG_BASE}.fields.sulphur`)}
                onFieldChange={updateFormData}
                maxDigits={5}
                decimals={3}
                adornment="%"
                thousandSeparator
                canBeginWithZero={false}
              />
            </div>
            <div className="xl:col-span-2">
              <FormItem<VesselNominationFormData, typeof FORM_FIELDS.MERCAPTAN>
                testId="nomination-mercaptan-input"
                name={FORM_FIELDS.MERCAPTAN}
                type="number"
                label={t(`${LANG_BASE}.fields.mercaptan`)}
                onFieldChange={updateFormData}
                maxDigits={8}
                decimals={3}
                adornment="ppm"
                thousandSeparator
                canBeginWithZero={false}
              />
            </div>
            <div className="xl:col-span-3">
              <FormItem<VesselNominationFormData, typeof FORM_FIELDS.STOWED_CARGO_TEMPERATURE>
                testId="nomination-stowed-cargo-temperature-input"
                name={FORM_FIELDS.STOWED_CARGO_TEMPERATURE}
                type="number"
                label={t(`${LANG_BASE}.fields.stowed-cargo-temperature`)}
                onFieldChange={updateFormData}
                maxDigits={6}
                decimals={3}
                adornment="°C"
                thousandSeparator
                canBeginWithZero={false}
              />
            </div>
            <div className="xl:col-span-3">
              <FormItem<VesselNominationFormData, typeof FORM_FIELDS.HYDROGEN_SULPHIDE>
                testId="nomination-hydrogen-sulphide-input"
                name={FORM_FIELDS.HYDROGEN_SULPHIDE}
                type="number"
                label={t(`${LANG_BASE}.fields.hydrogen-sulphide`)}
                onFieldChange={updateFormData}
                maxDigits={8}
                decimals={3}
                adornment="ppm"
                thousandSeparator
                canBeginWithZero={false}
              />
            </div>
            <div className="xl:col-span-2">
              <FormItem<VesselNominationFormData, typeof FORM_FIELDS.RVP>
                testId="nomination-rvp-input"
                name={FORM_FIELDS.RVP}
                type="number"
                label={t(`${LANG_BASE}.fields.rvp`)}
                onFieldChange={updateFormData}
                maxDigits={6}
                decimals={3}
                adornment="psi"
                thousandSeparator
                canBeginWithZero={false}
              />
            </div>
            <div className="xl:col-span-2">
              <FormItem<VesselNominationFormData, typeof FORM_FIELDS.COLOUR_ASTM>
                testId="nomination-colour-astm-input"
                name={FORM_FIELDS.COLOUR_ASTM}
                type="text"
                label={t(`${LANG_BASE}.fields.colour-astm`)}
                onFieldChange={updateFormData}
                maxChars={20}
              />
            </div>
            <div className="xl:col-span-2">
              <FormItem<VesselNominationFormData, typeof FORM_FIELDS.BENZENE_CONTENT>
                testId="nomination-benzene-content-input"
                name={FORM_FIELDS.BENZENE_CONTENT}
                type="number"
                label={t(`${LANG_BASE}.fields.benzene-content`)}
                onFieldChange={updateFormData}
                maxDigits={5}
                decimals={3}
                adornment="%"
                thousandSeparator
                canBeginWithZero={false}
              />
            </div>
            <div className="col-span-full flex">
              <FormItem<VesselNominationFormData, typeof FORM_FIELDS.ARE_HIGH_H2S_TANKS_ADEQUATE>
                testId="nomination-are-high-h2s-tanks-adequate-input"
                name={FORM_FIELDS.ARE_HIGH_H2S_TANKS_ADEQUATE}
                type="radio"
                label={t(`${LANG_BASE}.fields.are-high-h2s-tanks-adequate`)}
                onFieldChange={updateFormData}
                options={yesNoNAOptions}
                horizontalRadioButtons
              />
            </div>
            <div className="xl:col-span-3">
              <FormItem<
                VesselNominationFormData,
                typeof FORM_FIELDS.AVG_PRESSURE_CARGO_TANKS_ARRIVAL
              >
                testId="nomination-avg-pressure-cargo-tanks-arrival-input"
                name={FORM_FIELDS.AVG_PRESSURE_CARGO_TANKS_ARRIVAL}
                type="number"
                label={t(`${LANG_BASE}.fields.avg-pressure-cargo-tanks-arrival`)}
                onFieldChange={updateFormData}
                maxDigits={8}
                decimals={3}
                adornment="mmWG"
                thousandSeparator
                canBeginWithZero={false}
              />
            </div>
          </>
        )}
        <div className="xl:col-span-3">
          <FormItem<VesselNominationFormData, typeof FORM_FIELDS.MAX_PRESSURE_MANIFOLDS>
            testId="nomination-max-pressure-manifolds-input"
            name={FORM_FIELDS.MAX_PRESSURE_MANIFOLDS}
            type="number"
            label={t(`${LANG_BASE}.fields.max-pressure-manifolds`)}
            onFieldChange={updateFormData}
            maxDigits={6}
            decimals={3}
            adornment="bar"
            thousandSeparator
            canBeginWithZero={false}
          />
        </div>
        {isVesselConditionLoaded && (
          <div className="col-span-full flex">
            <FormItem<
              VesselNominationFormData,
              typeof FORM_FIELDS.IS_CRUDE_OIL_WASHING_OPERATION_PLANNED
            >
              testId="nomination-is-crude-oil-washing-operation-planned-input"
              name={FORM_FIELDS.IS_CRUDE_OIL_WASHING_OPERATION_PLANNED}
              type="radio"
              label={t(`${LANG_BASE}.fields.is-crude-oil-washing-operation-planned`)}
              onFieldChange={updateFormData}
              options={yesNAOptions}
              horizontalRadioButtons
            />
          </div>
        )}
      </FormSection>
    </FormProvider>
  );
};

export default ToxicComponent;
